// Which day the scheduling panel should open on.
//
// It opened on today, and the practice has same-day booking turned off, so the
// panel greeted everybody with "No available slots for this date" and waited
// for them to know to change it. Opening on a day that can actually be booked
// is the difference between a panel that works and one that looks broken.

const DAY_NAMES = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
];

/** How far ahead to look before giving up. A fortnight is well past any gap. */
const HORIZON_DAYS = 14;

const toInputValue = (date: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/**
 * The first day from `from` that the provider works and the practice allows.
 *
 * Unknown working days mean the mapping has not loaded yet: fall back to
 * tomorrow, which is probably bookable, rather than today, which is not when
 * same-day is off. And never search for ever — a provider whose days are all
 * unrecognised gets tomorrow rather than an infinite loop.
 */
export function nextBookableDate(
  from: Date,
  workingDays: string[] | undefined | null,
  sameDayAllowed: boolean,
): string {
  const worked = new Set((workingDays || []).map((d) => String(d).toLowerCase()));
  const startOffset = sameDayAllowed ? 0 : 1;

  if (worked.size === 0) {
    const fallback = new Date(from);
    fallback.setDate(fallback.getDate() + Math.max(startOffset, 1));
    return toInputValue(fallback);
  }

  for (let offset = startOffset; offset <= HORIZON_DAYS; offset += 1) {
    const candidate = new Date(from);
    candidate.setDate(candidate.getDate() + offset);
    if (worked.has(DAY_NAMES[candidate.getDay()])) return toInputValue(candidate);
  }

  const giveUp = new Date(from);
  giveUp.setDate(giveUp.getDate() + Math.max(startOffset, 1));
  return toInputValue(giveUp);
}
